import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Container,
    Divider,
    List,
    ListItem,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';

const BookingPrivacyPolicy = (): JSX.Element => {
    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={2} sx={{ p: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
                    OrderBuddy Privacy Policy
                </Typography>

                <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    Effective Date: September 23, 2025
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    Last Updated: September 23, 2025
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                    This Privacy Policy explains how OrderBuddy Inc. collects, uses, processes, and protects your personal information when you use our booking platform and services.
                </Alert>

                <Divider sx={{ mb: 4 }} />

                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            1. INFORMATION WE COLLECT
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            We collect information you provide directly to us, information we obtain automatically when you use our services, and information from third-party sources.
                        </Typography>

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            1.1 Information You Provide to Us
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="Account Information"
                                    secondary="Name, email address, phone number, password, and profile information"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Booking Information"
                                    secondary="Reservation details, preferences, special requests, and guest information"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Payment Information"
                                    secondary="Credit card details, billing address, and payment history (processed securely through our payment partners)"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Communication Data"
                                    secondary="Messages, reviews, feedback, and customer service interactions"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Identity Verification"
                                    secondary="Government-issued ID, passport information, or other verification documents when required"
                                />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            1.2 Information We Collect Automatically
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="Device Information"
                                    secondary="IP address, browser type, operating system, device identifiers, and mobile network information"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Usage Data"
                                    secondary="Pages visited, time spent on pages, search queries, booking patterns, and feature usage"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Location Information"
                                    secondary="Approximate location based on IP address and precise location if you grant permission"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Cookies and Tracking Technologies"
                                    secondary="Session cookies, persistent cookies, web beacons, and similar tracking technologies"
                                />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            1.3 Information from Third Parties
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="Social Media Platforms"
                                    secondary="Profile information when you connect your social media accounts"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Service Providers"
                                    secondary="Information from hotels, restaurants, and other partners to fulfill your bookings"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Marketing Partners"
                                    secondary="Information from advertising and analytics partners to improve our services"
                                />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            2. HOW WE USE YOUR INFORMATION
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            We use the information we collect for the following purposes:
                        </Typography>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            2.1 Service Provision
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Process and manage your bookings and reservations" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Facilitate communication between you and service providers" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Process payments and send booking confirmations" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Provide customer support and respond to inquiries" />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            2.2 Platform Improvement
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Analyze usage patterns to improve our platform and services" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Develop new features and functionality" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Personalize your experience and provide relevant recommendations" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Conduct research and analytics to understand user behavior" />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            2.3 Marketing and Communications
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Send promotional offers, newsletters, and marketing communications (with your consent)" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Provide information about new services and features" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Conduct surveys and market research" />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            2.4 Legal and Security
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Comply with legal obligations and regulatory requirements" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Prevent fraud, abuse, and security threats" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Enforce our terms of service and protect our rights" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Respond to legal requests and court orders" />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            3. INFORMATION SHARING AND DISCLOSURE
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            We may share your information in the following circumstances:
                        </Typography>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            3.1 Service Providers and Partners
                        </Typography>
                        <Typography variant="body1" paragraph>
                            We share necessary booking information with hotels, restaurants, and other service providers to fulfill your reservations. This includes your name, contact information, booking details, and special requests.
                        </Typography>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            3.2 Third-Party Service Providers
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Payment processors for secure transaction processing" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Cloud storage providers for data hosting and backup" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Analytics providers for platform improvement" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Customer service platforms for support operations" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Marketing service providers for promotional communications" />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            3.3 Legal Requirements
                        </Typography>
                        <Typography variant="body1" paragraph>
                            We may disclose your information when required by law, court order, or government regulation, or when we believe disclosure is necessary to:
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Comply with legal obligations" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Protect our rights, property, or safety" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Prevent fraud or illegal activities" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Respond to emergency situations" />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            3.4 Business Transfers
                        </Typography>
                        <Typography variant="body1" paragraph>
                            In the event of a merger, acquisition, sale of assets, or bankruptcy, your information may be transferred to the acquiring entity as part of the business transaction.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            4. DATA SECURITY AND PROTECTION
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            We implement comprehensive security measures to protect your personal information:
                        </Typography>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            4.1 Technical Safeguards
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="SSL/TLS encryption for data transmission" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="AES-256 encryption for data at rest" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Multi-factor authentication for administrative access" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Regular security audits and penetration testing" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Secure cloud infrastructure with redundancy and backup systems" />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            4.2 Administrative Safeguards
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Employee background checks and security training" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Role-based access controls and principle of least privilege" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Regular access reviews and deprovisioning procedures" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Incident response and breach notification procedures" />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            4.3 Physical Safeguards
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Secure data centers with biometric access controls" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="24/7 security monitoring and surveillance" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Environmental controls and disaster recovery systems" />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            5. YOUR PRIVACY RIGHTS AND CHOICES
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            Depending on your location, you may have the following rights regarding your personal information:
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 2, mb: 3 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Right</strong></TableCell>
                                        <TableCell><strong>Description</strong></TableCell>
                                        <TableCell><strong>How to Exercise</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>Access</TableCell>
                                        <TableCell>Request a copy of your personal information</TableCell>
                                        <TableCell>Contact privacy@orderbuddy.com</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Rectification</TableCell>
                                        <TableCell>Correct inaccurate or incomplete information</TableCell>
                                        <TableCell>Update in account settings or contact support</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Erasure</TableCell>
                                        <TableCell>Request deletion of your personal information</TableCell>
                                        <TableCell>Submit deletion request through account settings</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Portability</TableCell>
                                        <TableCell>Receive your data in a structured, machine-readable format</TableCell>
                                        <TableCell>Request data export through account settings</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Objection</TableCell>
                                        <TableCell>Object to processing for marketing or legitimate interests</TableCell>
                                        <TableCell>Opt-out links in emails or contact privacy@orderbuddy.com</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            5.1 Marketing Communications
                        </Typography>
                        <Typography variant="body1" paragraph>
                            You can opt out of marketing communications at any time by:
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Clicking the unsubscribe link in our emails" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Updating your preferences in your account settings" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Contacting our customer service team" />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            5.2 Cookies and Tracking
                        </Typography>
                        <Typography variant="body1" paragraph>
                            You can control cookies through your browser settings, but note that disabling certain cookies may affect platform functionality.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            6. DATA RETENTION AND DELETION
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy.
                        </Typography>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            6.1 Retention Periods
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="Account Information"
                                    secondary="Retained while your account is active and for 7 years after account closure for legal compliance"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Booking Data"
                                    secondary="Retained for 10 years for tax, accounting, and legal purposes"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Payment Information"
                                    secondary="Retained according to payment processor requirements and financial regulations"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Marketing Data"
                                    secondary="Retained until you opt out or for 3 years of inactivity"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Technical Logs"
                                    secondary="Retained for 1-2 years for security and troubleshooting purposes"
                                />
                            </ListItem>
                        </List>

                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            6.2 Secure Deletion
                        </Typography>
                        <Typography variant="body1" paragraph>
                            When we delete your information, we use industry-standard secure deletion methods to ensure data cannot be recovered or reconstructed.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            7. INTERNATIONAL DATA TRANSFERS
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            OrderBuddy operates globally, and your information may be transferred to and processed in countries other than your country of residence.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            We ensure appropriate safeguards are in place for international transfers, including:
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="European Commission-approved Standard Contractual Clauses" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Adequacy decisions by relevant data protection authorities" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Certification schemes and codes of conduct" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Binding corporate rules for intra-group transfers" />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            8. CHILDREN&apos;S PRIVACY
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information promptly.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Parents or guardians who believe we may have collected information from their child should contact us immediately at privacy@orderbuddy.com.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            9. UPDATES TO THIS PRIVACY POLICY
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            When we make material changes, we will:
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText primary="Post the updated policy on our platform with a new effective date" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Send email notification to registered users" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Provide prominent notice on our platform for 30 days" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Obtain consent for material changes where required by law" />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>
                <Divider sx={{ mt: 4, mb: 3 }} />

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        © 2025 OrderBuddy Inc. All rights reserved.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        This Privacy Policy is part of our Terms and Conditions and governs your use of the OrderBuddy platform.
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default BookingPrivacyPolicy;